if (!customElements.get('pdp-resource-carousel')) {
  customElements.define(
    'pdp-resource-carousel',
    class PdpResourceCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-resource-track]');
        this.cards = Array.from(this.querySelectorAll('[data-resource-card]'));
        this.dots = Array.from(this.querySelectorAll('[data-resource-dot]'));
        this.pagination = this.querySelector('[data-resource-pagination]');

        if (!this.track || this.cards.length < 1) return;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleDotClick = this.handleDotClick.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleTrackClick = this.handleTrackClick.bind(this);
        this.handleNativeDragStart = this.handleNativeDragStart.bind(this);

        this.activePointerId = null;
        this.dragStartX = 0;
        this.dragStartScrollLeft = 0;
        this.hasDragged = false;
        this.isPointerDown = false;
        this.suppressClick = false;
        this.raf = null;
        this.slidePositions = [];
        this.pendingDotIndex = null;
        this.pendingScrollTimeout = null;

        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.track.addEventListener('pointerdown', this.handlePointerDown);
        this.track.addEventListener('pointermove', this.handlePointerMove);
        this.track.addEventListener('pointerup', this.handlePointerUp);
        this.track.addEventListener('pointercancel', this.handlePointerUp);
        this.track.addEventListener('mousedown', this.handleMouseDown);
        this.track.addEventListener('mousemove', this.handleMouseMove);
        this.track.addEventListener('mouseup', this.handleMouseUp);
        this.track.addEventListener('mouseleave', this.handleMouseUp);
        this.track.addEventListener('click', this.handleTrackClick, true);
        this.track.addEventListener('dragstart', this.handleNativeDragStart);
        this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.handleResize);
        }

        this.refreshPagination();
      }

      disconnectedCallback() {
        if (this.track) {
          this.track.removeEventListener('scroll', this.handleScroll);
          this.track.removeEventListener('pointerdown', this.handlePointerDown);
          this.track.removeEventListener('pointermove', this.handlePointerMove);
          this.track.removeEventListener('pointerup', this.handlePointerUp);
          this.track.removeEventListener('pointercancel', this.handlePointerUp);
          this.track.removeEventListener('mousedown', this.handleMouseDown);
          this.track.removeEventListener('mousemove', this.handleMouseMove);
          this.track.removeEventListener('mouseup', this.handleMouseUp);
          this.track.removeEventListener('mouseleave', this.handleMouseUp);
          this.track.removeEventListener('click', this.handleTrackClick, true);
          this.track.removeEventListener('dragstart', this.handleNativeDragStart);
        }

        this.dots?.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));

        if (this.raf) {
          window.cancelAnimationFrame(this.raf);
          this.raf = null;
        }

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        } else {
          window.removeEventListener('resize', this.handleResize);
        }

        if (this.pendingScrollTimeout) {
          window.clearTimeout(this.pendingScrollTimeout);
          this.pendingScrollTimeout = null;
        }
      }

      handleScroll() {
        if (this.raf) return;

        this.raf = window.requestAnimationFrame(() => {
          this.updateActiveDot();
          this.raf = null;
        });
      }

      handleResize() {
        this.refreshPagination();
      }

      handleDotClick(event) {
        const index = this.dots.indexOf(event.currentTarget);

        this.scrollToPosition(index);
      }

      handlePointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;

        this.startDrag(event.pointerId, event.clientX);

        try {
          this.track.setPointerCapture?.(event.pointerId);
        } catch (error) {
          // Pointer capture is a progressive enhancement for smoother desktop dragging.
        }
      }

      handlePointerMove(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

        this.dragTo(event.clientX, event);
      }

      handlePointerUp(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

        try {
          this.track.releasePointerCapture?.(event.pointerId);
        } catch (error) {
          // Some browsers release pointer capture automatically on cancel.
        }

        this.endDrag();
      }

      handleMouseDown(event) {
        if (this.isPointerDown || event.button !== 0) return;

        this.startDrag('mouse', event.clientX);
      }

      handleMouseMove(event) {
        if (!this.isPointerDown || this.activePointerId !== 'mouse') return;

        this.dragTo(event.clientX, event);
      }

      handleMouseUp() {
        if (!this.isPointerDown || this.activePointerId !== 'mouse') return;

        this.endDrag();
      }

      startDrag(pointerId, clientX) {
        this.pendingDotIndex = null;
        if (this.pendingScrollTimeout) {
          window.clearTimeout(this.pendingScrollTimeout);
          this.pendingScrollTimeout = null;
        }

        this.activePointerId = pointerId;
        this.dragStartX = clientX;
        this.dragStartScrollLeft = this.track.scrollLeft;
        this.hasDragged = false;
        this.isPointerDown = true;
        this.suppressClick = false;
      }

      dragTo(clientX, event) {
        const distance = clientX - this.dragStartX;

        if (Math.abs(distance) > 3) {
          this.hasDragged = true;
          this.track.classList.add('is-dragging');
          event.preventDefault();
        }

        if (this.hasDragged) {
          this.track.scrollTo({
            left: this.dragStartScrollLeft - distance,
            behavior: 'auto',
          });
        }
      }

      endDrag() {
        const didDrag = this.hasDragged;
        const dragStartScrollLeft = this.dragStartScrollLeft;
        const dragEndScrollLeft = this.track.scrollLeft;

        this.track.classList.remove('is-dragging');
        this.suppressClick = didDrag;
        this.activePointerId = null;
        this.isPointerDown = false;
        this.hasDragged = false;

        if (didDrag) {
          this.settleDrag(dragStartScrollLeft, dragEndScrollLeft);
          return;
        }

        this.updateActiveDot();
      }

      handleTrackClick(event) {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }

      handleNativeDragStart(event) {
        if (!this.isPointerDown) return;

        event.preventDefault();
      }

      getMaxScrollLeft() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      getTrackPaddingLeft() {
        return Number.parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      }

      getCardStep() {
        if (this.cards.length > 1) {
          return Math.max(1, this.cards[1].offsetLeft - this.cards[0].offsetLeft);
        }

        return Math.max(1, this.cards[0]?.getBoundingClientRect().width || 1);
      }

      getVisibleCardCount() {
        if (this.cards.length < 2) return this.cards.length;

        const styles = window.getComputedStyle(this.track);
        const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
        const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
        const visibleWidth = Math.max(1, this.track.clientWidth - paddingLeft - paddingRight);
        const step = this.getCardStep();

        return Math.min(this.cards.length, Math.max(1, Math.ceil(visibleWidth / step)));
      }

      getClosestPositionIndex(scrollLeft) {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slidePositions.forEach((position, index) => {
          const distance = Math.abs(scrollLeft - position);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      scrollToPosition(index, behavior = 'smooth') {
        const targetLeft = this.slidePositions[index];

        if (index < 0 || typeof targetLeft !== 'number') return;

        if (this.pendingScrollTimeout) {
          window.clearTimeout(this.pendingScrollTimeout);
        }

        const shouldReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const scrollBehavior = shouldReduceMotion ? 'auto' : behavior;

        this.pendingDotIndex = index;
        this.setActiveDot(index);
        this.track.classList.add('is-stepping');
        this.track.scrollTo({
          left: targetLeft,
          behavior: scrollBehavior,
        });

        this.pendingScrollTimeout = window.setTimeout(() => {
          this.track.classList.remove('is-stepping');
          this.track.scrollTo({
            left: targetLeft,
            behavior: 'auto',
          });
          this.pendingDotIndex = null;
          this.pendingScrollTimeout = null;
          this.updateActiveDot();
        }, scrollBehavior === 'auto' ? 80 : 420);
      }

      settleDrag(startScrollLeft, endScrollLeft) {
        if (this.slidePositions.length < 2) {
          this.updateActiveDot();
          return;
        }

        const lastIndex = this.slidePositions.length - 1;
        const dragDistance = endScrollLeft - startScrollLeft;
        const startIndex = this.getClosestPositionIndex(startScrollLeft);
        const dragThreshold = Math.min(120, Math.max(40, this.getCardStep() * 0.18));
        let targetIndex = this.getClosestPositionIndex(endScrollLeft);

        if (Math.abs(dragDistance) >= dragThreshold) {
          if (dragDistance > 0) {
            targetIndex = Math.max(targetIndex, Math.min(lastIndex, startIndex + 1));
          } else {
            targetIndex = Math.min(targetIndex, Math.max(0, startIndex - 1));
          }
        } else {
          targetIndex = startIndex;
        }

        this.scrollToPosition(targetIndex);
      }

      refreshPagination() {
        // A resize invalidates an in-flight scroll target from the old layout.
        window.clearTimeout(this.pendingScrollTimeout);
        this.pendingScrollTimeout = null;
        this.pendingDotIndex = null;
        this.track.classList.remove('is-stepping');
        const maxScrollLeft = this.getMaxScrollLeft();
        const paddingLeft = this.getTrackPaddingLeft();

        // Keep one stop per card on phones and deduplicate the shared end
        // position when several cards fit on wider screens.
        this.slidePositions = this.cards
          .map((card) => Math.min(maxScrollLeft, Math.max(0, card.offsetLeft - paddingLeft)))
          .filter((position, index, positions) => index === 0 || position - positions[index - 1] > 1);

        this.dots.forEach((dot, index) => {
          const isVisible = index < this.slidePositions.length && this.slidePositions.length > 1;
          dot.hidden = !isVisible;
          dot.toggleAttribute('aria-hidden', !isVisible);
          dot.tabIndex = isVisible ? 0 : -1;
        });

        if (this.pagination) {
          this.pagination.hidden = this.slidePositions.length < 2;
        }

        this.updateActiveDot();
      }

      setActiveDot(activeIndex) {
        this.dots.forEach((dot, index) => {
          const isActive = index === activeIndex && !dot.hidden;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      updateActiveDot() {
        if (this.dots.length < 1 || this.slidePositions.length < 1) return;

        if (this.pendingDotIndex !== null) {
          const targetLeft = this.slidePositions[this.pendingDotIndex];

          this.setActiveDot(this.pendingDotIndex);

          if (typeof targetLeft === 'number' && Math.abs(this.track.scrollLeft - targetLeft) < 2) {
            this.pendingDotIndex = null;
          }

          return;
        }

        let activeIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.slidePositions.forEach((position, index) => {
          const distance = Math.abs(this.track.scrollLeft - position);

          if (distance < closestDistance) {
            closestDistance = distance;
            activeIndex = index;
          }
        });

        this.setActiveDot(activeIndex);
      }
    }
  );
}
